import { getMimeType, blobUrlToBase64, isBase64Image } from "@/utils/file";
import axios from "axios";
import { ServiceStatus } from "@/models/enums/service";

// Helper to get store and action dynamically
const dispatchFileUploadState = async (payload: any) => {
  const { default: store } = await import("@/stores");
  const { fileUploadSetState } = await import("@/stores/slices/plan/visit");
  store.dispatch(fileUploadSetState(payload));
};


const currentAPI = process.env.REACT_APP_FILE_BASE_DOMAIN;
const publicAPI = process.env.REACT_APP_FILE_BASE_DOMAIN_PUBLIC;
const storageAPI = process.env.REACT_APP_STORAGE_API_URL;

class APIFile {
  getImageUrl(id: any) {
    return `${publicAPI}/DocsAPIx64/api/v1/Document/PreviewPublicFile?folderFileId=${id}&domainId=${process.env.REACT_APP_DOMAIN_ID}`;
  }

  extractFileId(fileUrl: string) {
    let params = new URL(fileUrl).searchParams;
    return params.get("folderFileId");
  }
  async get(id: any) {
    if (!Number.isInteger(parseInt(id))) {
      return null;
    } else {
      try {
        if (window.isOffline) {
          return null;
        } else {
          const response = await axios({
            url: `${currentAPI}/DocsAPIx64/api/v1/Document/PreviewFile?folderFileId=${id}&domainId=${process.env.REACT_APP_DOMAIN_ID}`,
            method: "GET",
            responseType: "blob",
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          return url;
        }
      } catch (error) {
        throw error;
      }
    }
  }

  /**
   * Upload image to DocsAPI
   * @param image Image to upload (Base64 string or Blob URL)
   * @param concurrent Concurrent to upload
   * @returns 
   */
  async uploadImage(image: number | string, concurrent?: number | string) {
    const store = (await import("@/stores")).default;
    try {
      // If it's already a numeric ID or digit-only ID, no need to re-upload
      if (typeof image === "number" || (typeof image === "string" && /^\d+$/.test(image))) {
        return { folderFileId: image };
      }

      let base64Image = image;
      // If it is a blob URL, convert to Base64
      if (typeof image === "string" && image.startsWith("blob:")) {
        base64Image = await blobUrlToBase64(image);
      }

      // If it's not base64 and not a valid image format, log warning
      if (typeof base64Image !== "string" || !isBase64Image(base64Image)) {
        console.warn("uploadImage: payload is not a valid base64 image", base64Image);
      }

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.loading,
          error: undefined,
        },
      });
      let fileName = concurrent ? Date.now() + "_" + concurrent : Date.now();
      let body = JSON.stringify({
        folderId: process.env.REACT_APP_FOLDER_ID,
        domainId: process.env.REACT_APP_DOMAIN_ID,
        file: base64Image,
        fileName,
      });

      let headers = {
        accept: "*/*",
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("accessToken"),
      };

      let url = currentAPI + "/DocsAPIx64/api/v1/Document/UploadBase64";

      const res = await axios.post(url, body, {
        headers: headers,
      });

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.succeeded,
          error: undefined,
        },
      });
      return res.data;
    } catch (error) {
      let errMessage: string = (error as Error).message;

      if (axios.isAxiosError(error) && error.response && error.response?.data?.message) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${error.response.data.message}`;
      } else if (axios.isAxiosError(error) && error.response) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${JSON.stringify(error.response.data)}`;
      }

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.failed,
          error: errMessage,
        },
      });
      throw error;
    }
  }

  /**
   * Upload file
   * @param file File to upload
   * @param id userId
   * @returns
   */
  async uploadFile(file: File, id?: number | string) {
    const store = (await import("@/stores")).default;
    try {
      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.loading,
          error: undefined,
        },
      });
      const folderId = process.env.REACT_APP_FOLDER_FILE_ID!;
      const domainId = process.env.REACT_APP_DOMAIN_ID!;

      let formData = new FormData();
      formData.append("folderId", folderId);
      formData.append("domainId", domainId);
      formData.append("file", file);
      if (id !== undefined) formData.append("userId", id.toString());

      let headers = {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
        Authorization: "Bearer " + localStorage.getItem("accessToken"),
      };

      let url = currentAPI + "/DocsAPIx64/api/v1/Document/Upload";

      const res = await axios.post(url, formData, {
        headers: headers,
      });

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.succeeded,
          error: undefined,
        },
      });
      return res.data;
    } catch (error) {
      let errMessage: string = (error as Error).message;

      if (axios.isAxiosError(error) && error.response && error.response?.data?.message) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${error.response.data.message}`;
      } else if (axios.isAxiosError(error) && error.response) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${JSON.stringify(error.response.data)}`;
      }

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.failed,
          error: errMessage,
        },
      });
      throw error;
    }
  }

  /**
   * Load file and save to system storage
   * @param id File to download
   * @returns no return
   */
  async loadFile(id: any, accept: string[] = []) {
    if (!Number.isInteger(parseInt(id))) {
      return null;
    } else {
      try {
        const response = await axios({
          url: `${currentAPI}/DocsAPIx64/api/v1/Document/DownLoadFile?folderFileId=${id}&domainId=${process.env.REACT_APP_DOMAIN_ID}`,
          method: "GET",
          responseType: "blob",
        });

        if (accept.length > 0) {
          const contentType = response.headers["content-type"] || response.headers["Content-Type"];
          const extension = contentType ? "." + getMimeType(contentType) : "";
          const isValid = accept.some((type) => {
            if (type.startsWith(".")) {
              return type.toLowerCase() === extension.toLowerCase();
            }
            return contentType.includes(type);
          });

          if (!isValid) return null;
        }

        console.log(response);
        return response.data;
      } catch (error) {
        throw error;
      }
    }
  }

  /**
 * Download file and save to system storage
 * @param id File to download
 * @returns no return
 */
  async downloadFile(id: any) {
    if (!Number.isInteger(parseInt(id))) {
      return null;
    } else {
      try {
        const response = await axios({
          url: `${currentAPI}/DocsAPIx64/api/v1/Document/DownLoadFile?folderFileId=${id}&domainId=${process.env.REACT_APP_DOMAIN_ID}`,
          method: "GET",
          responseType: "blob",
        });

        const extension = response.headers["Content-Type"] ? "." + getMimeType(response.headers["Content-Type"]) : "";

        let filename = `business_review_actual-${id}${extension}`;

        let url = window.URL.createObjectURL(response.data);
        let a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        throw error;
      }
    }
  }

  /**
   * 
   * @param bucket must be "ka-checkin" for checkin or "ka-shopclose" for shopclose
   * @param image 
   * @returns 
   */
  async storageUpload64(bucket: string, image: number | string) {
    if (bucket !== "ka-checkin" && bucket !== "ka-shopclose") throw new Error("Invalid bucket name.")
    try {
      const fileType = getMimeType(image.toString().substring(image.toString().indexOf(":") + 1, image.toString().indexOf(";")));

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.loading,
          error: undefined,
        },
      });
      let body = JSON.stringify({
        base64: image,
        fileType
      });

      let headers = {
        accept: "*/*",
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("accessToken"),
      };

      let url = storageAPI + `/v1/buckets/${bucket}`;

      const res = await axios.post(url, body, {
        headers: headers,
      });
      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.succeeded,
          error: undefined,
        },
      });

      return res.data;
    } catch (error) {
      let errMessage: string = (error as Error).message;
      if (axios.isAxiosError(error) && error.response && error.response?.data?.message) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${error.response.data.message}`;
      } else if (axios.isAxiosError(error) && error.response) {
        errMessage = `รหัสสถานะ: ${error.response.status} ข้อความ: ${JSON.stringify(error.response.data)}`;
      }

      await dispatchFileUploadState({
        network: {
          code: ServiceStatus.failed,
          error: errMessage,
        },
      });
      throw error;
    }
  }
}

export default APIFile;
